import appConfig from "config/appConfig";
import { useTranslation } from "react-i18next";
import { IBookingPaymentModeType, ISettingsType } from "common/src/types";
import { Box, Grid, Card, CardContent, Typography, Chip } from "@mui/material";
import { AttachMoney, CreditCard, AccountBalanceWallet, Pix, Payments, QrCode, Receipt, Payment } from "@mui/icons-material";

interface PaymentSelectorProps {
    settings: ISettingsType | null;
    selected: IBookingPaymentModeType;
    onSelect: (method: IBookingPaymentModeType) => void;
}

export const PaymentSelector = ({ selected, onSelect, settings }: PaymentSelectorProps) => {
    const { t } = useTranslation();

    const getIcon = (method: IBookingPaymentModeType) => {
        switch (method) {
            case 'cash':
                return <AttachMoney />;
            case 'wallet':
                return <AccountBalanceWallet />;
            case 'PIX':
                return <Pix />;
            case 'MERCADO_PAGO':
                return <Payments />;
            case 'STRIPE':
                return <Payments />;
            case 'driver_pix':
                return <QrCode />;
            case 'driver_credit_card':
                return <CreditCard />;
            case 'driver_debit_card':
                return <Receipt />;
            default:
                return <Payment />;
        }
    };

    const getAvailableMethods = () => {
        const methods: { value: IBookingPaymentModeType; label: string; available: boolean }[] = [];

        methods.push({
            value: 'cash',
            label: t('cash'),
            available: !settings?.disable_cash,
        });

        methods.push({
            value: 'wallet',
            label: t('wallet'),
            available: !settings?.disable_wallet,
        });

        methods.push({
            value: 'PIX',
            label: t('pix'),
            available: !settings?.disable_online && appConfig?.REACT_APP_DEACTIVATE_USE_MERCADO_PAGO_PIX !== true,
        });

        methods.push({
            value: 'MERCADO_PAGO',
            label: t('MERCADO_PAGO'),
            available: !settings?.disable_online && appConfig?.REACT_APP_DEACTIVATE_USE_MERCADO_PAGO_PREFERENCE !== true,
        });

        methods.push({
            value: 'STRIPE',
            label: t('STRIPE'),
            available: !settings?.disable_online && appConfig?.REACT_APP_ACTIVE_USE_STRIPE === true,
        });


        const additionalPayments = settings?.additional_payments;
        if (additionalPayments) {
            let additionalPaymentsArray: string[] = [];

            if (typeof additionalPayments === "string") {
                try {
                    additionalPaymentsArray = JSON.parse(additionalPayments);
                } catch (e) { }
            } else if (Array.isArray(additionalPayments)) {
                additionalPaymentsArray = additionalPayments;
            }

            if (Array.isArray(additionalPaymentsArray)) {
                const driverPaymentKeys = ['driver_pix', 'driver_credit_card', 'driver_debit_card'];
                driverPaymentKeys.forEach(paymentKey => {
                    methods.push({
                        value: paymentKey as IBookingPaymentModeType,
                        label: t(paymentKey as any),
                        available: additionalPaymentsArray.includes(paymentKey),
                    });
                });
            }
        }

        return methods;
    };

    const methods = getAvailableMethods();

    return (
        <Box sx={{ height: "100%", p: 2, overflowY: "auto" }}>
            <Grid container spacing={2}>
                {methods.map((method) => {
                    const isSelected = selected === method.value;

                    return (
                        <Grid item xs={12} sm={6} md={4} key={method.value}>
                            <Card
                                onClick={() => { if (method.available) { onSelect(method.value); } }}
                                sx={{
                                    borderRadius: 4,
                                    overflow: "visible",
                                    position: 'relative',
                                    transition: 'all 0.2s',
                                    border: isSelected ? '2px solid' : '1px solid',
                                    cursor: method.available ? 'pointer' : 'default',
                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                    opacity: method.available ? (isSelected ? 1 : 0.7) : 0.4,
                                    '&:hover': method.available ? {
                                        opacity: 1,
                                        boxShadow: 3,
                                    } : {},
                                }}
                            >
                                {!method.available && (
                                    <Chip
                                        size="small"
                                        color="error"
                                        label={t("DISABLED")}
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            fontSize: '0.7rem',
                                            zIndex: 1,
                                        }}
                                    />
                                )}
                                <CardContent>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Box sx={{
                                                color: method.available ? (isSelected ? 'primary.main' : 'text.secondary') : 'text.disabled',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}>
                                                {getIcon(method.value)}
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    opacity: method.available ? 1 : 0.5,
                                                    fontWeight: isSelected ? 'bold' : 'normal',
                                                }}
                                            >
                                                {method.label}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};